import * as React from 'react';
import { Modal } from '@components/base/feedback';
import { Button } from '@components/base/actions';
import { Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

type ModalSize = 'sm' | 'md' | 'lg' | 'full';

const modalSizeDescriptions: Record<ModalSize, string> = {
  sm: '480px',
  md: '640px',
  lg: '800px',
  full: '95vw',
};

function ModalShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  // State for interactive preview modal
  const [previewOpen, setPreviewOpen] = React.useState(false);

  // State for size variant modals
  const [sizeModals, setSizeModals] = React.useState<Record<ModalSize, boolean>>({
    sm: false,
    md: false,
    lg: false,
    full: false,
  });

  // State for structure variant modals
  const [structureModals, setStructureModals] = React.useState({
    full: false,
    headerBody: false,
    bodyFooter: false,
    bodyOnly: false,
  });

  // State for scrollable modal
  const [scrollableOpen, setScrollableOpen] = React.useState(false);

  // State for close behavior modals
  const [closeBehaviorModals, setCloseBehaviorModals] = React.useState({
    noBackdrop: false,
    noEscape: false,
    noClose: false,
  });

  const openSizeModal = (size: ModalSize) => {
    setSizeModals((prev) => ({ ...prev, [size]: true }));
  };

  const closeSizeModal = (size: ModalSize) => {
    setSizeModals((prev) => ({ ...prev, [size]: false }));
  };

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="modal behavior">
          <Button onClick={() => { setPreviewOpen(true); }}>
            Open Modal
          </Button>

          <Modal
            open={previewOpen}
            onClose={() => { setPreviewOpen(false); }}
            size={playgroundProps.size as ModalSize}
            closeOnBackdropClick={playgroundProps.closeOnBackdropClick as boolean}
            closeOnEscape={playgroundProps.closeOnEscape as boolean}
          >
            <Modal.Header title="Policy Review" />
            <Modal.Body>
              <VStack gap={4}>
                <Text>
                  Please review the policy details below before proceeding with the
                  submission. Make sure all information is accurate and complete.
                </Text>
                <Text color="secondary">
                  Size: {playgroundProps.size as string}
                </Text>
                <Text color="secondary">
                  Close on backdrop: {String(playgroundProps.closeOnBackdropClick)}
                </Text>
                <Text color="secondary">
                  Close on escape: {String(playgroundProps.closeOnEscape)}
                </Text>
              </VStack>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setPreviewOpen(false); }}>
                Cancel
              </Button>
              <Button onClick={() => { setPreviewOpen(false); }}>
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Modals come in four sizes: sm (480px), md (640px), lg (800px), and full (95vw)"
       
      >
          <HStack gap={4} wrap="wrap">
            {(['sm', 'md', 'lg', 'full'] as const).map((size) => (
              <React.Fragment key={size}>
                <Button variant="secondary" onClick={() => { openSizeModal(size); }}>
                  {size.toUpperCase()} Modal
                </Button>

                <Modal
                  open={sizeModals[size]}
                  onClose={() => { closeSizeModal(size); }}
                  size={size}
                >
                  <Modal.Header title={`${size.toUpperCase()} Size Modal`} />
                  <Modal.Body>
                    <Text>
                      This is a {size} modal with a max-width of{' '}
                      {modalSizeDescriptions[size]}.
                    </Text>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button onClick={() => { closeSizeModal(size); }}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
              </React.Fragment>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Structure Options */}
      <ShowcaseDemoSection
        title="Structure Options"
        description="Modals can include different combinations of header, body, and footer"
       
      >
          <HStack gap={4} wrap="wrap">
            {/* Full structure */}
            <Button variant="secondary" onClick={() => { setStructureModals((prev) => ({ ...prev, full: true })); }}>
              Header + Body + Footer
            </Button>
            <Modal
              open={structureModals.full}
              onClose={() => { setStructureModals((prev) => ({ ...prev, full: false })); }}
            >
              <Modal.Header title="Complete Modal" />
              <Modal.Body>
                <Text>This modal includes header, body, and footer sections.</Text>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => { setStructureModals((prev) => ({ ...prev, full: false })); }}>
                  Cancel
                </Button>
                <Button onClick={() => { setStructureModals((prev) => ({ ...prev, full: false })); }}>
                  Confirm
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Header + Body only */}
            <Button variant="secondary" onClick={() => { setStructureModals((prev) => ({ ...prev, headerBody: true })); }}>
              Header + Body
            </Button>
            <Modal
              open={structureModals.headerBody}
              onClose={() => { setStructureModals((prev) => ({ ...prev, headerBody: false })); }}
            >
              <Modal.Header title="Information" />
              <Modal.Body>
                <Text>
                  This modal only has a header and body. It is useful for displaying
                  information where no action is required from the user.
                </Text>
              </Modal.Body>
            </Modal>

            {/* Body + Footer only */}
            <Button variant="secondary" onClick={() => { setStructureModals((prev) => ({ ...prev, bodyFooter: true })); }}>
              Body + Footer
            </Button>
            <Modal
              open={structureModals.bodyFooter}
              onClose={() => { setStructureModals((prev) => ({ ...prev, bodyFooter: false })); }}
            >
              <Modal.Body>
                <Text weight="semibold" size="lg" className="mb-2">
                  Delete this claim?
                </Text>
                <Text color="secondary">
                  This action cannot be undone. The claim and all associated
                  documents will be permanently removed.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => { setStructureModals((prev) => ({ ...prev, bodyFooter: false })); }}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={() => { setStructureModals((prev) => ({ ...prev, bodyFooter: false })); }}>
                  Delete Claim
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Body only */}
            <Button variant="secondary" onClick={() => { setStructureModals((prev) => ({ ...prev, bodyOnly: true })); }}>
              Body Only
            </Button>
            <Modal
              open={structureModals.bodyOnly}
              onClose={() => { setStructureModals((prev) => ({ ...prev, bodyOnly: false })); }}
            >
              <Modal.Body>
                <VStack gap={4} align="center" className="py-4">
                  <Text weight="semibold" size="lg">
                    Quick confirmation
                  </Text>
                  <Text color="secondary" className="text-center">
                    Click anywhere outside or press Escape to close this modal.
                  </Text>
                </VStack>
              </Modal.Body>
            </Modal>
          </HStack>
      </ShowcaseDemoSection>

      {/* Scrollable Content */}
      <ShowcaseDemoSection
        title="Scrollable Content"
        description="Modal body scrolls when content exceeds the maximum height"
       
      >
          <Button variant="secondary" onClick={() => { setScrollableOpen(true); }}>
            Open Scrollable Modal
          </Button>

          <Modal
            open={scrollableOpen}
            onClose={() => { setScrollableOpen(false); }}
            size="md"
          >
            <Modal.Header title="Terms and Conditions" />
            <Modal.Body>
              <VStack gap={4}>
                {Array.from({ length: 20 }, (_, i) => (
                  <Text key={i}>
                    <Text as="span" weight="semibold">Section {i + 1}:</Text>{' '}
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                    eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                    enim ad minim veniam, quis nostrud exercitation ullamco laboris
                    nisi ut aliquip ex ea commodo consequat.
                  </Text>
                ))}
              </VStack>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setScrollableOpen(false); }}>
                Decline
              </Button>
              <Button onClick={() => { setScrollableOpen(false); }}>
                Accept Terms
              </Button>
            </Modal.Footer>
          </Modal>
      </ShowcaseDemoSection>

      {/* Close Behavior */}
      <ShowcaseDemoSection
        title="Close Behavior"
        description="Configure how the modal can be closed"
       
      >
          <HStack gap={4} wrap="wrap">
            {/* No backdrop click */}
            <Button variant="secondary" onClick={() => { setCloseBehaviorModals((prev) => ({ ...prev, noBackdrop: true })); }}>
              No Backdrop Close
            </Button>
            <Modal
              open={closeBehaviorModals.noBackdrop}
              onClose={() => { setCloseBehaviorModals((prev) => ({ ...prev, noBackdrop: false })); }}
              closeOnBackdropClick={false}
            >
              <Modal.Header title="Backdrop Close Disabled" />
              <Modal.Body>
                <Text>
                  Clicking outside this modal will not close it.
                  Use the close button or press Escape to dismiss.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => { setCloseBehaviorModals((prev) => ({ ...prev, noBackdrop: false })); }}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* No escape key */}
            <Button variant="secondary" onClick={() => { setCloseBehaviorModals((prev) => ({ ...prev, noEscape: true })); }}>
              No Escape Close
            </Button>
            <Modal
              open={closeBehaviorModals.noEscape}
              onClose={() => { setCloseBehaviorModals((prev) => ({ ...prev, noEscape: false })); }}
              closeOnEscape={false}
            >
              <Modal.Header title="Escape Key Disabled" />
              <Modal.Body>
                <Text>
                  Pressing the Escape key will not close this modal.
                  Use the close button or click outside to dismiss.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => { setCloseBehaviorModals((prev) => ({ ...prev, noEscape: false })); }}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* No automatic close */}
            <Button variant="secondary" onClick={() => { setCloseBehaviorModals((prev) => ({ ...prev, noClose: true })); }}>
              Button Only Close
            </Button>
            <Modal
              open={closeBehaviorModals.noClose}
              /* v8 ignore start */
              onClose={() => { setCloseBehaviorModals((prev) => ({ ...prev, noClose: false })); }}
              /* v8 ignore stop */
              closeOnBackdropClick={false}
              closeOnEscape={false}
            >
              <Modal.Header title="Required Confirmation" showCloseButton={false} />
              <Modal.Body>
                <Text>
                  This modal can only be closed by clicking the button below.
                  Useful for critical confirmations that require explicit user action.
                </Text>
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => { setCloseBehaviorModals((prev) => ({ ...prev, noClose: false })); }}>
                  I Understand
                </Button>
              </Modal.Footer>
            </Modal>
          </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const ModalShowcase: ShowcaseDefinition = {
  id: 'modal',
  name: 'Modal',
  category: 'feedback',
  description: 'Dialog overlay for focused interactions and confirmations',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg', 'full'],
      defaultValue: 'md',
      description: 'Width of the modal (sm=480px, md=640px, lg=800px, full=95vw)',
    },
    {
      name: 'closeOnBackdropClick',
      type: 'boolean',
      defaultValue: true,
      description: 'Whether clicking the backdrop closes the modal',
    },
    {
      name: 'closeOnEscape',
      type: 'boolean',
      defaultValue: true,
      description: 'Whether pressing Escape closes the modal',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="dialog"',
      'aria-modal="true"',
      'aria-labelledby (title)',
    ],
    keyboardNav: [
      'Escape: Close modal (configurable)',
      'Tab: Trap focus within modal',
    ],
    notes: [
      'Focus is trapped inside modal when open',
      'Focus returns to trigger element on close',
      'Body scroll is locked when modal is open',
    ],
  },
  component: ModalShowcaseComponent,
};
